import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ToastProvider } from './components/ui/Toast';
import { ProgressBar } from './components/ui/ProgressBar';
import ErrorBoundary from './components/ErrorBoundary';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import MerchPage from './pages/MerchPage';
import DashboardPage from './pages/DashboardPage';
import FundingRatesDashboard from './pages/FundingRatesDashboard';
import NotFoundPage from './pages/NotFoundPage';
import ContactPage from './pages/ContactPage';

const DealsPage = lazy(() => import('./pages/DealsPage'));

// Scroll restoration component
function ScrollToTop() {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 3,
      retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
      staleTime: 30000, // Consider data fresh for 30 seconds
      gcTime: 5 * 60 * 1000, // Cache for 5 minutes
      refetchInterval: 60000, // Refetch every minute
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <Router>
            <ErrorBoundary>
              <ToastProvider>
                <ScrollToTop />
                <ProgressBar />
                <Navbar />
                <Suspense fallback={<ProgressBar />}>
                  <main className="flex-grow">
                    <Routes>
                      <Route path="/" element={<HomePage />} />
                      <Route path="/about" element={<AboutPage />} />
                      <Route path="/merch" element={<MerchPage />} />
                      <Route path="/dashboard" element={<DashboardPage />} />
                      <Route path="/funding-rates" element={<FundingRatesDashboard />} />
                      <Route path="/contact" element={<ContactPage />} />
                      <Route
                        path="/deals"
                        element={
                          <Suspense fallback={<ProgressBar />}>
                            <DealsPage />
                          </Suspense>
                        }
                      />
                      <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                  </main>
                </Suspense>
                <Footer />
              </ToastProvider>
            </ErrorBoundary>
          </Router>
        </ToastProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;